import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { TranslateService } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { UnitConverter } from '../../../../pipes/unit-conversion';

import {
  Client,
  OnDemandAttributeDataOut,
  OnDemandDetailsDataIn,
  PageInfo,
  PermissionsEnum,
} from '../../../../api/api-reference';
import { AuthService } from '../../../../services/auth.service';
import { SignalRService } from '../../../../services/signalr.service';
import { PaginationComponent } from '../../../core/components/pagination/pagination.component';
import { ClipboardService } from '../../../../services/clipboard.service';
import { SignalRBaseComponent } from '../../../../services/signalr-base.component';

@Component({
  selector: 'app-custom-register-details',
  standalone: true,
  imports: [
    TranslateModule,
    AngularSvgIconModule,
    DatePipe,
    UnitConverter,
    CommonModule,
    RouterLink,
    PaginationComponent,
  ],
  templateUrl: './custom-register-details.component.html',
  styleUrl: './custom-register-details.component.scss',
})
export class CustomRegisterDetailsComponent extends SignalRBaseComponent implements OnInit, OnDestroy {
  data: OnDemandAttributeDataOut[] = [];
  commandId?: string;
  meterNo?: string;
  PermissionEnum = PermissionsEnum;
  public currentPage: number = 1;
  public numberOfRegisters: number = 0; // total count from backend
  public lastPageSize: number = 0;
  public pageSize: number = 10;

  requestData = new OnDemandDetailsDataIn({
    commandId: '',
    pageInfo: new PageInfo({ pageSize: this.pageSize, page: this.currentPage }),
  });

  constructor(
    private toastr: ToastrService,
    private client: Client,
    private route: ActivatedRoute,
    public authService: AuthService,
    public clipboardService: ClipboardService,
    signalRService: SignalRService,
    private translate: TranslateService,
  ) {
    super(signalRService);
    this.route.queryParams.subscribe((params) => {
      this.commandId = params['commandId'];
      this.meterNo = params['meterNo'];
      this.requestData.commandId = params['commandId'];
    });
    this.loadTable(1);
  }

protected override registerHandlers(): void {
    if (this.meterNo) {
      this.subscribeToDevices([this.meterNo]);
    }

    this.registerSignalREvent('on-demand-generic-attribute-read', (message) => {
      if (message.commandId !== this.commandId) return;

      let parsedValues: any;
      try {
        if (message.serializedValues) {
          parsedValues = JSON.parse(message.serializedValues);
        }
      } catch (err) {
        console.error('Failed to parse serializedValues:', err);
        return;
      }

      if (message.commandStatus === 'InProgress' && Array.isArray(parsedValues)) {
        this.numberOfRegisters += parsedValues.length;
        this.lastPageSize = Math.ceil(this.numberOfRegisters / this.pageSize);

        // Update local table view if there is space on current page
        if (this.data.length < this.pageSize) {
          const spaceLeft = this.pageSize - this.data.length;
          const toAdd = parsedValues.slice(0, spaceLeft);

          const newEntries = toAdd.map((attr: any) =>
            OnDemandAttributeDataOut.fromJS({
              ingestionTime: attr.IngestionTime,
              meterTime: attr.MeterTime,
              meterNumber: attr.MeterNumber ?? this.meterNo,
              attributeName: attr.AttributeName,
              stringValue: attr.StringValue,
              doubleValue: attr.DoubleValue,
              errorMessage: attr.ErrorMessage,
              unit: attr.Unit,
              obis: attr.Obis,
            })
          );
          this.data = [...this.data, ...newEntries];
        }
      } else if (message.commandStatus === 'Finalize') {
        // Re-sync with database to ensure UI is 100% accurate
        this.loadTable(this.currentPage);
      }
    });

    // 3. Silence common broadcast warnings
    this.registerSignalREvent('online-status', () => {});
  }

  override async ngOnInit(): Promise<void> {
    await super.ngOnInit();
  }

  override async ngOnDestroy(): Promise<void> {
    await super.ngOnDestroy();
  }

  loadTable(page?: number) {
    this.currentPage = page || 1;
    if (page) this.requestData.pageInfo!.page = this.currentPage;

    this.client.getOnDemandRegisterReads(this.requestData).subscribe({
      next: (response) => {
        this.data = response.data ?? [];
        this.numberOfRegisters = response.count ?? 0;
        this.lastPageSize = Math.ceil(this.numberOfRegisters / this.pageSize);
      },
      error: (err) => {
        this.toastr.error(err);
      },
    });
  }

  pageChange(value: number) {
    this.currentPage = value;
    this.loadTable(this.currentPage);
  }

  getErrorMessage(jsonString: string): string {
    try {
      const parsed = JSON.parse(jsonString);
      return parsed?.Message || 'Unknown error';
    } catch {
      return jsonString;
    }
  }

  copyErrorMessage(error: string) {
    const textToCopy = this.getErrorMessage(error);
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        const translatedMessage = this.translate.instant(
          'ERROR_COPIED_TO_CLIPBOARD',
        );
        this.toastr.success(translatedMessage);
      })
      .catch((err) => {
        console.error('Failed to copy: ', err);
      });
  }
}
